import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { logisticsService } from '../services/api';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const LogisticsHandoffPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ porEnrutar: [], enrutados: [] });
    const [summary, setSummary] = useState([]);
    const [selectedOrders, setSelectedOrders] = useState(new Set());
    const [activeTab, setActiveTab] = useState('backlog'); // 'backlog', 'dispatched', 'summary'
    const [searchQuery, setSearchQuery] = useState('');
    const [dispatchedRoutes, setDispatchedRoutes] = useState([]);
    const [dispatchedDate, setDispatchedDate] = useState(new Date().toISOString().split('T')[0]);
    const [expandedRoutes, setExpandedRoutes] = useState(new Set());

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [resp, sumResp] = await Promise.all([
                logisticsService.getHandoffOrders(),
                logisticsService.getHandoffSummary()
            ]);
            if (resp.success) setData(resp.data);
            if (sumResp.success) setSummary(sumResp.data);
        } catch (e) {
            console.error('Error loading handoff data:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadDispatchedRoutes = useCallback(async (date) => {
        setLoading(true);
        try {
            const response = await fetch(`/api/logistics/handoff/summary?date=${date}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();
            if (data.success) {
                setDispatchedRoutes(data.data);
            }
        } catch (e) {
            console.error('Error loading dispatched routes:', e);
            toast.error('Error cargando rutas despachadas');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
        const interval = setInterval(loadData, 30000); // 30s refresh
        return () => clearInterval(interval);
    }, [loadData]);

    useEffect(() => {
        if (activeTab === 'dispatched') {
            loadDispatchedRoutes(dispatchedDate);
        }
    }, [activeTab, dispatchedDate, loadDispatchedRoutes]);

    const handleToggleOrder = (id) => {
        const next = new Set(selectedOrders);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedOrders(next);
    };

    const handleSelectAllInGroup = (orders) => {
        const orderIds = orders.map(o => o.id);
        const allSelected = orderIds.every(id => selectedOrders.has(id));
        const next = new Set(selectedOrders);

        if (allSelected) {
            orderIds.forEach(id => next.delete(id));
        } else {
            orderIds.forEach(id => next.add(id));
        }
        setSelectedOrders(next);
    };

    const handleConfirmHandoff = async () => {
        if (selectedOrders.size === 0) return;
        try {
            setLoading(true);
            const resp = await logisticsService.confirmHandoff(Array.from(selectedOrders));
            if (resp.success) {
                toast.success(resp.message);
                setSelectedOrders(new Set());
                loadData();
            } else {
                toast.error(resp.message || 'Error confirmando entrega física');
            }
        } catch (e) {
            toast.error('Error confirmando entrega física');
        } finally {
            setLoading(false);
        }
    };

    // Calculate financial stats for an order
    const getOrderFinancialType = (order) => {
        const totalAmount = parseFloat(order.total_amount || 0);
        const paidAmount = parseFloat(order.paid_amount || 0);
        const paymentMethod = String(order.payment_method || '').toLowerCase();

        // 1. Check if it's a credit order (payment method contains "credito" or "crédito")
        if (paymentMethod.includes('credito') || paymentMethod.includes('crédito')) {
            return { type: 'credit', amount: totalAmount };
        }

        // 2. Check if it's fully paid
        if (paidAmount >= totalAmount) {
            return { type: 'paid', amount: totalAmount };
        }

        // 3. Everything else is "to collect" (contraentrega, partial payments, etc.)
        return { type: 'toCollect', amount: totalAmount - paidAmount };
    };

    // Group orders by messenger with financial breakdown
    const groupedByMessenger = useMemo(() => {
        const filtered = searchQuery
            ? data.porEnrutar.filter(o =>
                String(o.order_number).toLowerCase().includes(searchQuery.toLowerCase()) ||
                String(o.customer_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                String(o.messenger_name || '').toLowerCase().includes(searchQuery.toLowerCase())
            )
            : data.porEnrutar;

        const groups = {};
        filtered.forEach(order => {
            const key = order.assigned_messenger_id || 'unassigned';
            if (!groups[key]) {
                groups[key] = {
                    messenger_id: key,
                    messenger_name: order.messenger_name || 'Sin asignar',
                    orders: [],
                    stats: {
                        count: 0,
                        total: 0,
                        paid: 0,
                        toCollect: 0,
                        credit: 0
                    }
                };
            }

            groups[key].orders.push(order);
            groups[key].stats.count++;
            groups[key].stats.total += parseFloat(order.total_amount || 0);

            const finType = getOrderFinancialType(order);
            if (finType.type === 'paid') {
                groups[key].stats.paid += finType.amount;
            } else if (finType.type === 'toCollect') {
                groups[key].stats.toCollect += finType.amount;
            } else if (finType.type === 'credit') {
                groups[key].stats.credit += finType.amount;
            }
        });

        return Object.values(groups);
    }, [data.porEnrutar, searchQuery]);

    // Format currency
    const fmt = (val) => new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        maximumFractionDigits: 0
    }).format(val);

    // Messenger Group Component
    const MessengerGroup = ({ group }) => {
        const orderIds = group.orders.map(o => o.id);
        const allSelected = orderIds.every(id => selectedOrders.has(id));
        const someSelected = orderIds.some(id => selectedOrders.has(id));
        const selectedCount = orderIds.filter(id => selectedOrders.has(id)).length;

        return (
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-200 overflow-hidden mb-6">
                {/* Group Header */}
                <div className="bg-gradient-to-r from-slate-50 to-white px-8 py-6 border-b border-slate-100">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                                <Icons.User className="w-6 h-6 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-slate-900 tracking-tight">
                                    {group.messenger_name}
                                </h3>
                                <p className="text-sm text-slate-500 font-medium">
                                    {group.stats.count} pedidos • {fmt(group.stats.total)} total
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => handleSelectAllInGroup(group.orders)}
                            className={`px-6 py-3 rounded-2xl font-bold text-sm transition-all flex items-center gap-2 ${allSelected
                                ? 'bg-blue-600 text-white shadow-lg shadow-blue-100'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                        >
                            <Icons.CheckSquare className="w-4 h-4" />
                            {allSelected ? `Todos (${group.stats.count})` : someSelected ? `${selectedCount}/${group.stats.count}` : 'Seleccionar Todos'}
                        </button>
                    </div>

                    {/* Financial Breakdown */}
                    <div className="mt-4 flex gap-4 flex-wrap">
                        {group.stats.paid > 0 && (
                            <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-xl border border-green-100">
                                <Icons.CheckCircle2 className="w-4 h-4 text-green-600" />
                                <div>
                                    <div className="text-[10px] font-black text-green-600 uppercase tracking-wider">Pagado</div>
                                    <div className="text-sm font-bold text-green-700">{fmt(group.stats.paid)}</div>
                                </div>
                            </div>
                        )}
                        {group.stats.toCollect > 0 && (
                            <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 rounded-xl border border-orange-100">
                                <Icons.Coins className="w-4 h-4 text-orange-600" />
                                <div>
                                    <div className="text-[10px] font-black text-orange-600 uppercase tracking-wider">Por Cobrar</div>
                                    <div className="text-sm font-bold text-orange-700">{fmt(group.stats.toCollect)}</div>
                                </div>
                            </div>
                        )}
                        {group.stats.credit > 0 && (
                            <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-xl border border-blue-100">
                                <Icons.FileText className="w-4 h-4 text-blue-600" />
                                <div>
                                    <div className="text-[10px] font-black text-blue-600 uppercase tracking-wider">Crédito</div>
                                    <div className="text-sm font-bold text-blue-700">{fmt(group.stats.credit)}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Orders Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-slate-50/50 border-b border-slate-100">
                                <th className="px-6 py-4 w-12"></th>
                                <th className="px-6 py-4 text-xs font-black text-slate-400 uppercase tracking-widest">Orden</th>
                                <th className="px-6 py-4 text-xs font-black text-slate-400 uppercase tracking-widest">Cliente</th>
                                <th className="px-6 py-4 text-xs font-black text-slate-400 uppercase tracking-widest">Valor</th>
                                <th className="px-6 py-4 text-xs font-black text-slate-400 uppercase tracking-widest">Estado Pago</th>
                                <th className="px-6 py-4 text-xs font-black text-slate-400 uppercase tracking-widest">Detalles Pago</th>
                                <th className="px-6 py-4 text-xs font-black text-slate-400 uppercase tracking-widest">Aceptación</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {group.orders.map(order => {
                                const isSelected = selectedOrders.has(order.id);
                                const finType = getOrderFinancialType(order);

                                return (
                                    <tr key={order.id} className={`transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                                        <td className="px-4 py-3">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => handleToggleOrder(order.id)}
                                                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                                            />
                                        </td>
                                        <td className="px-4 py-3 font-bold text-gray-900 cursor-pointer" onClick={() => handleToggleOrder(order.id)}>
                                            #{order.order_number}
                                        </td>
                                        <td className="px-4 py-3 cursor-pointer" onClick={() => handleToggleOrder(order.id)}>
                                            <div className="font-medium text-gray-800">{order.customer_name}</div>
                                            <div className="text-xs text-gray-500 truncate max-w-xs">{order.customer_address}</div>
                                        </td>
                                        <td className="px-4 py-3 font-semibold text-blue-700">
                                            {fmt(order.total_amount)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 w-fit ${finType.type === 'paid' ? 'bg-green-100 text-green-800' :
                                                finType.type === 'toCollect' ? 'bg-orange-100 text-orange-800' :
                                                    'bg-blue-100 text-blue-800'
                                                }`}>
                                                {finType.type === 'paid' && <Icons.CheckCircle2 className="w-3 h-3" />}
                                                {finType.type === 'toCollect' && <Icons.Coins className="w-3 h-3" />}
                                                {finType.type === 'credit' && <Icons.FileText className="w-3 h-3" />}
                                                {finType.type === 'paid' ? 'Pagado' : finType.type === 'toCollect' ? 'Cobrar' : 'Crédito'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col gap-1">
                                                {parseFloat(order.paid_amount || 0) > 0 ? (
                                                    <>
                                                        <span className="text-xs font-bold text-green-700">
                                                            {fmt(order.paid_amount)}
                                                        </span>
                                                        <span className="text-[10px] text-gray-500 font-medium capitalize">
                                                            {order.payment_method || 'N/A'}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className="text-xs text-gray-400 italic">Sin pago</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-col">
                                                <span className="text-[10px] text-gray-400 uppercase font-bold">Aceptado</span>
                                                <span className="text-xs text-gray-600">
                                                    {order.accepted_at ? new Date(order.accepted_at).toLocaleString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: true }) : '-'}
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="p-4 md:p-8 bg-[#F8FAFC] min-h-screen">
            {/* Header */}
            <div className="mb-8 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/ready-to-deliver')}
                        className="p-2 hover:bg-white rounded-xl border border-transparent hover:border-slate-200 transition-all text-slate-400 hover:text-slate-600"
                    >
                        <Icons.ChevronLeft className="w-6 h-6" />
                    </button>
                    <div className="w-14 h-14 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center">
                        <Icons.PackageCheck className="w-8 h-8 text-blue-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-slate-900 tracking-tight">
                            Handoff de Logística
                        </h1>
                        <p className="text-slate-500 text-sm font-medium">Control de salida física a mensajería local</p>
                    </div>
                </div>

                <div className="flex bg-white rounded-2xl p-1.5 shadow-sm border border-slate-200">
                    <button
                        onClick={() => setActiveTab('backlog')}
                        className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 flex items-center gap-2 ${activeTab === 'backlog' ? 'bg-slate-900 text-white shadow-lg shadow-slate-200' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                        <Icons.LayoutList className="w-4 h-4" />
                        Por Despachar ({data.porEnrutar.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('dispatched')}
                        className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 flex items-center gap-2 ${activeTab === 'dispatched' ? 'bg-slate-900 text-white shadow-lg shadow-slate-200' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                        <Icons.Truck className="w-4 h-4" />
                        Rutas Despachadas
                    </button>
                    <button
                        onClick={() => setActiveTab('summary')}
                        className={`px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 flex items-center gap-2 ${activeTab === 'summary' ? 'bg-slate-900 text-white shadow-lg shadow-slate-200' : 'text-slate-500 hover:bg-slate-50'}`}
                    >
                        <Icons.PieChart className="w-4 h-4" />
                        Resumen Hoy
                    </button>
                </div>
            </div>

            {activeTab === 'backlog' ? (
                <div className="max-w-7xl mx-auto space-y-6">
                    {/* Controls */}
                    <div className="bg-white p-5 rounded-[2rem] shadow-sm border border-slate-200 flex flex-col md:flex-row gap-4 items-center justify-between">
                        <div className="relative w-full md:w-[400px]">
                            <Icons.Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Buscar por orden, cliente o mensajero..."
                                className="w-full pl-12 pr-4 py-3 bg-slate-50 border-transparent border focus:border-blue-500 focus:bg-white rounded-2xl outline-none transition-all text-sm font-medium"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <div className="flex gap-3 w-full md:w-auto">
                            <button
                                onClick={loadData}
                                disabled={loading}
                                className="aspect-square flex items-center justify-center p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-slate-500 hover:bg-slate-100 transition-all"
                                title="Actualizar"
                            >
                                <Icons.RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin text-blue-500' : ''}`} />
                            </button>
                            <button
                                disabled={selectedOrders.size === 0 || loading}
                                onClick={handleConfirmHandoff}
                                className="flex-1 md:flex-none px-8 py-3.5 bg-blue-600 text-white font-black rounded-2xl shadow-xl shadow-blue-100 hover:bg-blue-700 disabled:opacity-50 disabled:shadow-none transition-all flex items-center justify-center gap-3 transform active:scale-95"
                            >
                                <Icons.ArrowRightCircle className="w-5 h-5" />
                                Confirmar Despacho ({selectedOrders.size})
                            </button>
                        </div>
                    </div>

                    {/* Messenger Groups */}
                    {groupedByMessenger.length === 0 ? (
                        <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-200 p-24">
                            <div className="flex flex-col items-center gap-3">
                                <Icons.SearchX className="w-12 h-12 text-slate-200" />
                                <p className="text-slate-400 font-bold">
                                    {searchQuery ? 'No se encontraron resultados' : 'No hay pedidos aceptados esperando despacho físico'}
                                </p>
                            </div>
                        </div>
                    ) : (
                        groupedByMessenger.map(group => (
                            <MessengerGroup key={group.messenger_id} group={group} />
                        ))
                    )}
                </div>
            ) : activeTab === 'dispatched' ? (
                /* Dispatched Routes Section */
                <div className="max-w-7xl mx-auto space-y-6">
                    {/* Date Selector */}
                    <div className="bg-white p-5 rounded-[2rem] shadow-sm border border-slate-200 flex flex-col md:flex-row gap-4 items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Icons.Calendar className="w-5 h-5 text-slate-400" />
                            <input
                                type="date"
                                value={dispatchedDate}
                                onChange={(e) => setDispatchedDate(e.target.value)}
                                className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 transition-all text-sm font-medium"
                            />
                        </div>
                        <button
                            onClick={() => loadDispatchedRoutes(dispatchedDate)}
                            disabled={loading}
                            className="aspect-square flex items-center justify-center p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-slate-500 hover:bg-slate-100 transition-all"
                            title="Actualizar"
                        >
                            <Icons.RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin text-blue-500' : ''}`} />
                        </button>
                    </div>

                    {/* Dispatched Routes List */}
                    {dispatchedRoutes.length === 0 ? (
                        <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-200 p-24">
                            <div className="flex flex-col items-center gap-3">
                                <Icons.PackageX className="w-12 h-12 text-slate-200" />
                                <p className="text-slate-400 font-bold">
                                    No hay rutas despachadas para esta fecha
                                </p>
                            </div>
                        </div>
                    ) : (
                        dispatchedRoutes.map(route => (
                            <div key={route.messenger_id} className="bg-white rounded-[2.5rem] shadow-sm border border-slate-200 overflow-hidden">
                                {/* Messenger Header */}
                                <div className="px-8 py-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center">
                                                <Icons.User className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-black text-slate-800">{route.messenger_name}</h3>
                                                <p className="text-sm text-slate-500 font-medium">{route.orders_count} pedidos despachados</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-2xl font-black text-slate-900">{fmt(route.total_value)}</p>
                                            <p className="text-xs text-slate-500 font-bold uppercase">Total Despachado</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Financial Breakdown */}
                                <div className="px-8 py-6 bg-slate-50/50 border-b border-slate-100">
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="bg-white rounded-2xl p-4 border border-green-100">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Icons.CheckCircle className="w-4 h-4 text-green-600" />
                                                <span className="text-[10px] font-black text-slate-400 uppercase">Pagado</span>
                                            </div>
                                            <p className="text-xl font-black text-green-700">{fmt(route.total_paid || 0)}</p>
                                        </div>
                                        <div className="bg-white rounded-2xl p-4 border border-orange-100">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Icons.DollarSign className="w-4 h-4 text-orange-600" />
                                                <span className="text-[10px] font-black text-slate-400 uppercase">Por Cobrar</span>
                                            </div>
                                            <p className="text-xl font-black text-orange-700">{fmt(route.total_to_collect || 0)}</p>
                                        </div>
                                        <div className="bg-white rounded-2xl p-4 border border-blue-100">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Icons.CreditCard className="w-4 h-4 text-blue-600" />
                                                <span className="text-[10px] font-black text-slate-400 uppercase">Crédito</span>
                                            </div>
                                            <p className="text-xl font-black text-blue-700">{fmt((route.total_value - route.total_paid - route.total_to_collect) || 0)}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Payment Methods */}
                                {route.payment_methods && (
                                    <div className="px-8 py-4 bg-white border-b border-slate-100">
                                        <div className="flex items-center gap-2">
                                            <Icons.Wallet className="w-4 h-4 text-slate-400" />
                                            <span className="text-xs font-bold text-slate-500">Métodos de pago:</span>
                                            <span className="text-xs font-medium text-slate-700">{route.payment_methods}</span>
                                        </div>
                                    </div>
                                )}

                                {/* Expand/Print Actions */}
                                <div className="px-8 py-4 bg-slate-50 flex gap-3">
                                    <button
                                        onClick={() => {
                                            const next = new Set(expandedRoutes);
                                            if (next.has(route.messenger_id)) {
                                                next.delete(route.messenger_id);
                                            } else {
                                                next.add(route.messenger_id);
                                            }
                                            setExpandedRoutes(next);
                                        }}
                                        className="flex-1 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
                                    >
                                        {expandedRoutes.has(route.messenger_id) ? (
                                            <>
                                                <Icons.ChevronUp className="w-4 h-4" />
                                                Ocultar Detalle
                                            </>
                                        ) : (
                                            <>
                                                <Icons.ChevronDown className="w-4 h-4" />
                                                Ver Detalle ({route.orders?.length || 0} pedidos)
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => {
                                            const printContent = document.getElementById(`route-print-${route.messenger_id}`);
                                            if (printContent) {
                                                const printWindow = window.open('', '', 'width=800,height=600');
                                                printWindow.document.write('<html><head><title>Hoja de Ruta - ' + route.messenger_name + '</title>');
                                                printWindow.document.write('<style>body{font-family:Arial,sans-serif;padding:20px;}table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ddd;padding:8px;text-align:left;}th{background-color:#f2f2f2;}.header{margin-bottom:20px;}.total{font-weight:bold;margin-top:20px;}</style>');
                                                printWindow.document.write('</head><body>');
                                                printWindow.document.write(printContent.innerHTML);
                                                printWindow.document.write('</body></html>');
                                                printWindow.document.close();
                                                printWindow.print();
                                            }
                                        }}
                                        className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 transition-all flex items-center gap-2"
                                    >
                                        <Icons.Printer className="w-4 h-4" />
                                        Imprimir
                                    </button>
                                </div>

                                {/* Detailed Orders List */}
                                {expandedRoutes.has(route.messenger_id) && route.orders && (
                                    <div className="px-8 py-6 bg-white">
                                        <div id={`route-print-${route.messenger_id}`}>
                                            <div className="header mb-6">
                                                <h2 className="text-2xl font-black text-slate-900">Hoja de Ruta - {route.messenger_name}</h2>
                                                <p className="text-sm text-slate-500">Fecha de despacho: {new Date(dispatchedDate).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                                                <p className="text-sm text-slate-500">Total pedidos: {route.orders.length}</p>
                                            </div>
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="bg-slate-100">
                                                        <th className="px-4 py-3 text-left font-black text-[10px] uppercase text-slate-600">#</th>
                                                        <th className="px-4 py-3 text-left font-black text-[10px] uppercase text-slate-600">Pedido</th>
                                                        <th className="px-4 py-3 text-left font-black text-[10px] uppercase text-slate-600">Cliente</th>
                                                        <th className="px-4 py-3 text-left font-black text-[10px] uppercase text-slate-600">Teléfono</th>
                                                        <th className="px-4 py-3 text-left font-black text-[10px] uppercase text-slate-600">Dirección</th>
                                                        <th className="px-4 py-3 text-right font-black text-[10px] uppercase text-slate-600">Valor</th>
                                                        <th className="px-4 py-3 text-center font-black text-[10px] uppercase text-slate-600">Pago</th>
                                                        <th className="px-4 py-3 text-center font-black text-[10px] uppercase text-slate-600">Estado</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100">
                                                    {route.orders.map((order, idx) => {
                                                        const isPaid = parseFloat(order.paid_amount || 0) >= parseFloat(order.total_amount || 0);
                                                        const isCredit = String(order.payment_method || '').toLowerCase().includes('credito') || String(order.payment_method || '').toLowerCase().includes('crédito');
                                                        const toCollect = parseFloat(order.total_amount || 0) - parseFloat(order.paid_amount || 0);

                                                        return (
                                                            <tr key={order.id} className="hover:bg-slate-50">
                                                                <td className="px-4 py-3 font-bold text-slate-600">{idx + 1}</td>
                                                                <td className="px-4 py-3 font-mono font-bold text-blue-600">{order.order_number}</td>
                                                                <td className="px-4 py-3 font-medium text-slate-800">{order.customer_name}</td>
                                                                <td className="px-4 py-3 text-slate-600">{order.customer_phone}</td>
                                                                <td className="px-4 py-3 text-slate-600 text-xs">{order.customer_address}</td>
                                                                <td className="px-4 py-3 text-right font-bold text-slate-900">{fmt(order.total_amount)}</td>
                                                                <td className="px-4 py-3 text-center">
                                                                    {isPaid ? (
                                                                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-[10px] font-black">PAGADO</span>
                                                                    ) : isCredit ? (
                                                                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-[10px] font-black">CRÉDITO</span>
                                                                    ) : (
                                                                        <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-lg text-[10px] font-black">COBRAR {fmt(toCollect)}</span>
                                                                    )}
                                                                </td>
                                                                <td className="px-4 py-3 text-center">
                                                                    {order.messenger_status === 'delivered' ? (
                                                                        <Icons.CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                                                                    ) : (
                                                                        <Icons.Clock className="w-5 h-5 text-orange-500 mx-auto" />
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                            <div className="total mt-6 pt-4 border-t border-slate-200">
                                                <div className="grid grid-cols-3 gap-4 text-sm">
                                                    <div>
                                                        <p className="text-slate-500 font-medium">Total Despachado:</p>
                                                        <p className="text-xl font-black text-slate-900">{fmt(route.total_value)}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-slate-500 font-medium">Pagado:</p>
                                                        <p className="text-xl font-black text-green-700">{fmt(route.total_paid)}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-slate-500 font-medium">Por Cobrar:</p>
                                                        <p className="text-xl font-black text-orange-700">{fmt(route.total_to_collect)}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            ) : (
                /* Summary Section - Keep existing code */
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-8 space-y-6">
                        <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-200 overflow-hidden">
                            <div className="px-8 py-6 border-b border-slate-100 bg-slate-50/30 flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <Icons.Files className="w-5 h-5 text-slate-400" />
                                    <h3 className="font-black text-slate-800 tracking-tight">Planilla de Despacho (Hoy)</h3>
                                </div>
                                <div className="px-3 py-1 bg-white border border-slate-200 rounded-full text-[10px] font-black text-slate-500 uppercase">
                                    {new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })}
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                                            <th className="px-8 py-4">Mensajero</th>
                                            <th className="px-8 py-4 text-center">Cant.</th>
                                            <th className="px-8 py-4">Valor Despachado</th>
                                            <th className="px-8 py-4">Recaudo Pendiente</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {summary.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" className="px-8 py-16 text-center italic text-slate-400">
                                                    Aún no se han confirmado despachos físicos hoy.
                                                </td>
                                            </tr>
                                        ) : (
                                            summary.map(s => (
                                                <tr key={s.messenger_id} className="hover:bg-slate-50/50 transition-colors">
                                                    <td className="px-8 py-5">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center font-black text-slate-500 text-xs">
                                                                {s.messenger_name?.[0]}
                                                            </div>
                                                            <span className="font-bold text-slate-700">{s.messenger_name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-8 py-5 text-center">
                                                        <span className="bg-blue-600 text-white text-xs px-2.5 py-1 rounded-lg font-black">{s.orders_count}</span>
                                                    </td>
                                                    <td className="px-8 py-5 font-bold text-slate-600">
                                                        {fmt(s.total_value)}
                                                    </td>
                                                    <td className="px-8 py-5">
                                                        <div className="flex flex-col">
                                                            <span className="text-orange-600 font-black">
                                                                {fmt(s.total_to_collect)}
                                                            </span>
                                                            <span className="text-[10px] text-slate-400 font-bold uppercase truncate max-w-[120px]">
                                                                {s.payment_methods || 'N/A'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-4 space-y-6">
                        {/* Stats Cards */}
                        <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white shadow-2xl shadow-slate-300 relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/20 rounded-full -mr-16 -mt-16 blur-3xl group-hover:bg-blue-500/30 transition-all"></div>
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center">
                                    <Icons.TrendingUp className="w-6 h-6 text-blue-400" />
                                </div>
                                <h4 className="font-bold text-slate-400 uppercase tracking-widest text-xs">Total Despachado</h4>
                            </div>
                            <div className="text-4xl font-black mb-2 tracking-tighter">
                                {fmt(summary.reduce((acc, s) => acc + Number(s.total_value || 0), 0))}
                            </div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full text-blue-300 text-[10px] font-black uppercase tracking-wider">
                                <Icons.Package className="w-3 h-3" />
                                {summary.reduce((acc, s) => acc + Number(s.orders_count || 0), 0)} Pedidos confirmados
                            </div>
                        </div>

                        <div className="bg-white rounded-[2.5rem] p-8 border border-slate-200 shadow-sm">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center">
                                    <Icons.Coins className="w-6 h-6 text-orange-600" />
                                </div>
                                <h4 className="font-bold text-slate-400 uppercase tracking-widest text-xs">Cartera en Ruta</h4>
                            </div>
                            <div className="text-4xl font-black text-slate-900 mb-2 tracking-tighter">
                                {fmt(summary.reduce((acc, s) => acc + Number(s.total_to_collect || 0), 0))}
                            </div>
                            <p className="text-sm text-slate-500 font-medium leading-relaxed">
                                Valor total pendiente de cobro por parte de los mensajeros actualmente en ruta.
                            </p>
                        </div>

                        <div className="p-6 bg-blue-50 rounded-[2rem] border border-blue-100 flex items-start gap-4">
                            <div className="p-2 bg-blue-600 rounded-lg text-white">
                                <Icons.AlertCircle className="w-5 h-5" />
                            </div>
                            <div>
                                <h5 className="font-black text-blue-900 text-sm mb-1 uppercase tracking-tight">Importante</h5>
                                <p className="text-blue-700 text-xs leading-relaxed font-medium">
                                    Solo los pedidos con estado <strong>ENTREGADO FÍSICAMENTE</strong> afectarán el recaudo esperado del mensajero.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LogisticsHandoffPage;
